import { apiRequest } from "./api.client";

// ─── Types ────────────────────────────────────────────────────────────────────

export type AttachmentType = "voice_note" | "photo" | "screen_recording";

export interface UploadUrlFile {
  attachment_type: AttachmentType;
  file_name:       string;
  mime_type:       string;
  file_size_bytes: number;
}

export interface UploadUrlItem {
  s3_key:          string;
  attachment_type: AttachmentType;
  upload_url:      string | null;
}

export interface UploadUrlsResponse {
  files: UploadUrlItem[];
}

export interface ConfirmAttachmentItem {
  s3_key:          string;
  attachment_type: AttachmentType;
  file_name:       string;
  mime_type:       string;
  file_size_bytes: number;
}

export interface CreateTicketPayload {
  subject:     string;
  description: string;
  attachments: ConfirmAttachmentItem[];
}

export interface TicketAttachment {
  attachment_id:   string;
  attachment_type: AttachmentType;
  file_name:       string;
  mime_type:       string | null;
  file_size_bytes: number | null;
  uploaded_at:     string;
  message_id:      string | null;
  view_url:        string | null;
}

export type TicketStatus = "open" | "in_progress" | "resolved" | "closed";

export interface TicketMessage {
  message_id:  string;
  sender_role: "clinic" | "admin";
  sender_name: string;
  body:        string;
  created_at:  string;
  /** Unsent by its sender: body and attachments are gone, a placeholder remains. */
  is_deleted:  boolean;
  attachments: TicketAttachment[];
}

export interface ClinicInfo {
  clinic_id:      string;
  name:           string;
  location:       string | null;
  address:        string | null;
  phone:          string | null;
  email:          string | null;
  website:        string | null;
  license_number: string | null;
  company_size:   string | null;
}

export interface ReporterInfo {
  name:                string | null;
  email:               string | null;
  phone:               string | null;
  role:                string | null;
  designation:         string | null;
  registration_number: string | null;
}

export interface UnreadTicketItem {
  ticket_id:    string;
  subject:      string;
  clinic_name?: string | null;
  unread_count: number;
}

export interface SupportUnreadSummary {
  total:   number;
  tickets: UnreadTicketItem[];
}

export interface SupportTicket {
  ticket_id:      string;
  user_id:        string;
  clinic_id:      string | null;
  reporter_name:  string;
  reporter_email: string;
  clinic_name?:   string | null;
  /** Only populated on the platform-admin detail fetch. */
  clinic?:        ClinicInfo | null;
  reporter?:      ReporterInfo | null;
  subject:        string;
  description:    string;
  status:         TicketStatus;
  admin_notes:    string | null;
  created_at:     string;
  updated_at:     string;
  resolved_at:    string | null;
  attachments:    TicketAttachment[];
  messages:       TicketMessage[];
  /** Replies from the other party this viewer hasn't opened yet. */
  unread_count:   number;
}

// ─── S3 direct upload ─────────────────────────────────────────────────────────

/** PUT a file/blob directly to S3 using the presigned URL. No auth headers — S3 handles it. */
export async function uploadBlobToS3(presignedUrl: string, blob: Blob, contentType: string): Promise<void> {
  const res = await fetch(presignedUrl, {
    method: "PUT",
    headers: { "Content-Type": contentType },
    body: blob,
  });
  if (!res.ok) throw new Error(`S3 PUT failed: ${res.status}`);
}

// ─── API ──────────────────────────────────────────────────────────────────────

export const SupportTicketAPI = {
  requestUploadUrls(files: UploadUrlFile[]): Promise<UploadUrlsResponse> {
    return apiRequest<UploadUrlsResponse>("/support-tickets/upload-urls", {
      method: "POST",
      body: JSON.stringify({ files }),
    });
  },

  create(payload: CreateTicketPayload): Promise<SupportTicket> {
    return apiRequest<SupportTicket>("/support-tickets", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  listMine(): Promise<SupportTicket[]> {
    return apiRequest<SupportTicket[]>("/support-tickets");
  },

  get(ticketId: string): Promise<SupportTicket> {
    return apiRequest<SupportTicket>(`/support-tickets/${ticketId}`);
  },

  unreadSummary(): Promise<SupportUnreadSummary> {
    return apiRequest<SupportUnreadSummary>("/support-tickets/unread-summary");
  },

  markRead(ticketId: string): Promise<void> {
    return apiRequest<void>(`/support-tickets/${ticketId}/read`, { method: "POST" });
  },

  edit(
    ticketId: string,
    subject: string,
    description: string,
    removeAttachmentIds: string[] = [],
    addAttachments: ConfirmAttachmentItem[] = [],
  ): Promise<SupportTicket> {
    return apiRequest<SupportTicket>(`/support-tickets/${ticketId}`, {
      method: "PATCH",
      body: JSON.stringify({
        subject,
        description,
        remove_attachment_ids: removeAttachmentIds,
        add_attachments: addAttachments,
      }),
    });
  },

  remove(ticketId: string): Promise<void> {
    return apiRequest<void>(`/support-tickets/${ticketId}`, { method: "DELETE" });
  },

  unsend(ticketId: string, messageId: string): Promise<void> {
    return apiRequest<void>(`/support-tickets/${ticketId}/messages/${messageId}`, { method: "DELETE" });
  },

  markAllRead(): Promise<void> {
    return apiRequest<void>("/support-tickets/read-all", { method: "POST" });
  },

  reply(ticketId: string, body: string, attachments: ConfirmAttachmentItem[] = []): Promise<TicketMessage> {
    return apiRequest<TicketMessage>(`/support-tickets/${ticketId}/messages`, {
      method: "POST",
      body: JSON.stringify({ body, attachments }),
    });
  },
};

export const PlatformSupportTicketAPI = {
  list(status?: TicketStatus): Promise<SupportTicket[]> {
    const qs = status ? `?status=${encodeURIComponent(status)}` : "";
    return apiRequest<SupportTicket[]>(`/platform/support-tickets${qs}`);
  },

  get(ticketId: string): Promise<SupportTicket> {
    return apiRequest<SupportTicket>(`/platform/support-tickets/${ticketId}`);
  },

  unreadSummary(): Promise<SupportUnreadSummary> {
    return apiRequest<SupportUnreadSummary>("/platform/support-tickets/unread-summary");
  },

  markRead(ticketId: string): Promise<void> {
    return apiRequest<void>(`/platform/support-tickets/${ticketId}/read`, { method: "POST" });
  },

  remove(ticketId: string): Promise<void> {
    return apiRequest<void>(`/platform/support-tickets/${ticketId}`, { method: "DELETE" });
  },

  unsend(ticketId: string, messageId: string): Promise<void> {
    return apiRequest<void>(`/platform/support-tickets/${ticketId}/messages/${messageId}`, { method: "DELETE" });
  },

  markAllRead(): Promise<void> {
    return apiRequest<void>("/platform/support-tickets/read-all", { method: "POST" });
  },

  requestUploadUrls(files: UploadUrlFile[]): Promise<UploadUrlsResponse> {
    return apiRequest<UploadUrlsResponse>("/platform/support-tickets/upload-urls", {
      method: "POST",
      body: JSON.stringify({ files }),
    });
  },

  reply(ticketId: string, body: string, attachments: ConfirmAttachmentItem[] = []): Promise<TicketMessage> {
    return apiRequest<TicketMessage>(`/platform/support-tickets/${ticketId}/messages`, {
      method: "POST",
      body: JSON.stringify({ body, attachments }),
    });
  },

  update(ticketId: string, status: TicketStatus, adminNotes?: string): Promise<SupportTicket> {
    return apiRequest<SupportTicket>(`/platform/support-tickets/${ticketId}`, {
      method: "PATCH",
      body: JSON.stringify({ status, admin_notes: adminNotes }),
    });
  },
};
